import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Activity, Loader2, Target, Phone, Mail, CheckSquare, Calendar,
  FolderKanban, LayoutGrid, UserPlus, FileText, Sparkles, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_ICONS = {
  lead_created: { icon: Target, color: "text-blue-500 bg-blue-50" },
  lead_status_changed: { icon: Target, color: "text-violet-500 bg-violet-50" },
  lead_deleted: { icon: Target, color: "text-red-500 bg-red-50" },
  call_logged: { icon: Phone, color: "text-emerald-500 bg-emerald-50" },
  email_sent: { icon: Mail, color: "text-sky-500 bg-sky-50" },
  task_created: { icon: CheckSquare, color: "text-amber-500 bg-amber-50" },
  task_completed: { icon: CheckSquare, color: "text-emerald-600 bg-emerald-50" },
  appointment_created: { icon: Calendar, color: "text-blue-500 bg-blue-50" },
  appointment_booked: { icon: Calendar, color: "text-emerald-500 bg-emerald-50" },
  project_created: { icon: FolderKanban, color: "text-violet-500 bg-violet-50" },
  card_created: { icon: LayoutGrid, color: "text-indigo-500 bg-indigo-50" },
  card_moved: { icon: LayoutGrid, color: "text-amber-500 bg-amber-50" },
  member_joined: { icon: UserPlus, color: "text-emerald-500 bg-emerald-50" },
  note_added: { icon: FileText, color: "text-slate-500 bg-slate-50" },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ActivityItem({ activity }) {
  const config = ACTIVITY_ICONS[activity.activity_type] || { icon: Activity, color: "text-slate-500 bg-slate-50" };
  const Icon = config.icon;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`activity-${activity.activity_id}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800">
          <span className="font-medium">{activity.user_name}</span>{" "}
          <span className="text-slate-600">{activity.title}</span>
        </p>
        {activity.description && (
          <p className="text-xs text-slate-400 mt-0.5 truncate">{activity.description}</p>
        )}
      </div>
      <span className="text-[11px] text-slate-400 flex-shrink-0 whitespace-nowrap">{timeAgo(activity.created_at)}</span>
    </div>
  );
}

export function ActivityFeedWidget() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/activity-feed/recent`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { activities: [] })
      .then(d => setActivities(d.activities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;

  if (activities.length === 0) {
    return (
      <div className="py-6 text-center">
        <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No recent activity</p>
      </div>
    );
  }

  return (
    <div data-testid="activity-feed-widget">
      {activities.map(a => <ActivityItem key={a.activity_id} activity={a} />)}
    </div>
  );
}

export default function ActivityFeedPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => { fetchActivities(); }, [offset]);

  const fetchActivities = async () => {
    try {
      const res = await fetch(`${API}/activity-feed?limit=30&offset=${offset}`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        if (offset === 0) {
          setActivities(d.activities || []);
        } else {
          setActivities(prev => [...prev, ...(d.activities || [])]);
        }
        setTotal(d.total || 0);
      }
    } catch (e) { toast.error("Failed to load activity"); }
    finally { setLoading(false); }
  };

  if (loading && offset === 0) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="activity-feed-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Activity className="w-7 h-7 text-primary" />
          Activity Feed
        </h1>
        <p className="text-slate-500 mt-1">{total} total activities</p>
      </div>

      <Card>
        <CardContent className="pt-4">
          {activities.length === 0 ? (
            <div className="py-12 text-center">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No activity yet. Start by creating leads, logging calls, or booking appointments.</p>
            </div>
          ) : (
            <>
              {activities.map(a => <ActivityItem key={a.activity_id} activity={a} />)}
              {activities.length < total && (
                <div className="pt-4 text-center">
                  <Button variant="outline" size="sm" onClick={() => setOffset(activities.length)} data-testid="load-more-btn">
                    <ChevronDown className="w-4 h-4 mr-1" /> Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
